import { useState } from "react";
import { Link } from "react-router-dom";
import { Plus, ShoppingBag, FileText, ArrowUpRight } from "lucide-react";

/**
 * Design mock — Supply cloaked catalog card.
 * Route: /admin/mockups/cloaked-card
 *
 * Sections:
 *   1. Full-size comparison (rest vs hover)      — 380px wide
 *   2. Live grid  — three SKUs, one image        — ~380px wide
 *   3. Compact grid — 5 SKUs per row             — ~228px wide (production target)
 *   4. Supply palette test                       — oxblood/bone tint on same image
 */

const CARD_BG =
  "https://customer-assets-0z36b82j.emergentagent.net/job_979d7a0d-77c8-4766-b5d5-31216fe2063b/artifacts/wv8pulpr_bbk%20product%20card.png";

const CARD_BG_SUPPLY =
  "https://customer-assets-lqy194kg.emergentagent.net/job_c26c4ddf-0b99-4af9-b8f4-6f4e27981906/artifacts/0j9faakn_image.png";

const SAMPLE_PRODUCTS = [
  { sku: "BBK-TB500-5MG",  name: "TB-500",  subtitle: "Thymosin β-4 Fragment",     aminoCount: 7,  sequencePrefix: "Ac-LKKTETQ", price: 65,  vialMg: 5   },
  { sku: "BBK-BPC157-5MG", name: "BPC-157", subtitle: "Body Protection Compound",  aminoCount: 15, sequencePrefix: "GEPPPGKPA",  price: 65,  vialMg: 5   },
  { sku: "BBK-GHK-CU-50MG",name: "GHK-Cu",  subtitle: "Copper Tripeptide",         aminoCount: 3,  sequencePrefix: "Gly-His-Lys", price: 40, vialMg: 50  },
  { sku: "BBK-SEMA-5MG",   name: "Semax",   subtitle: "Heptapeptide",              aminoCount: 7,  sequencePrefix: "MEHFPGP",     price: 100, vialMg: 5   },
  { sku: "BBK-KLOW-BLEND", name: "KLOW",    subtitle: "4-Peptide Blend",           aminoCount: 24, sequencePrefix: "Blend",       price: 150, vialMg: 10  },
];

const THEMES = {
  select: {
    text:      "#0F2E3E",   // deep navy
    accent:    "#C9A24B",   // brand gold
    ctaBg:     "#0F2E3E",
    ctaHover:  "#C9A24B",
    imageTint: null,
    bg:        CARD_BG,
  },
  supply: {
    text:      "#4A0E17",   // oxblood
    accent:    "#C9A24B",   // brand gold pairs beautifully with the warm cream
    ctaBg:     "#4A0E17",
    ctaHover:  "#C9A24B",
    imageTint: null,        // new image is already warm, no tint needed
    bg:        CARD_BG_SUPPLY,
  },
};

export default function SupplyMockupCloakedCard() {
  const [hoverIdx, setHoverIdx] = useState(null);
  const [compactIdx, setCompactIdx] = useState(null);
  const [supplyIdx, setSupplyIdx] = useState(null);

  return (
    <div className="min-h-screen bg-bone px-6 py-14 lg:py-20" data-testid="mockup-cloaked-page">
      <div className="max-w-[1240px] mx-auto">
        <Link to="/admin" className="inline-flex items-center gap-2 text-[12px] uppercase tracking-[0.22em] text-stone hover:text-charcoal mb-8">
          ← Back to admin
        </Link>

        <p className="overline text-oxblood mb-3">Design mock · v4 · size + palette</p>
        <h1 className="font-serif text-5xl md:text-6xl tracking-tight mb-4">
          Cloaked catalog card.
        </h1>
        <p className="text-[15px] text-charcoal/80 leading-relaxed max-w-2xl mb-14">
          One background image, dozens of SKUs. Text layer is dynamic per
          product. At rest, the card shows brand + RUO only. Hover reveals
          the peptide identity for cleared users.
        </p>

        {/* ---- SECTION 1: Full-size comparison ---- */}
        <p className="overline text-oxblood mb-3">Section 1 · Full-size states</p>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-16 max-w-[820px]">
          <div>
            <p className="font-mono text-[11px] uppercase tracking-[0.24em] text-charcoal/60 mb-3">
              At rest · cloaked
            </p>
            <PeptideCard product={SAMPLE_PRODUCTS[0]} reveal={false} theme="select" />
          </div>
          <div>
            <p className="font-mono text-[11px] uppercase tracking-[0.24em] text-charcoal/60 mb-3">
              On hover · revealed
            </p>
            <PeptideCard product={SAMPLE_PRODUCTS[0]} reveal={true} theme="select" />
          </div>
        </div>

        {/* ---- SECTION 2: 3-up live grid ---- */}
        <p className="overline text-oxblood mb-3">Section 2 · Live 3-up grid · hover any card</p>
        <h2 className="font-serif text-3xl md:text-4xl tracking-tight mb-6">
          Try it — three SKUs, one image.
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-20">
          {SAMPLE_PRODUCTS.slice(0, 3).map((product, idx) => (
            <PeptideCard
              key={product.sku}
              product={product}
              reveal={hoverIdx === idx}
              interactive
              theme="select"
              onEnter={() => setHoverIdx(idx)}
              onLeave={() => setHoverIdx((p) => (p === idx ? null : p))}
            />
          ))}
        </div>

        {/* ---- SECTION 3: Compact 5-per-row grid ---- */}
        <p className="overline text-oxblood mb-3">Section 3 · Compact 5-per-row · production target size</p>
        <h2 className="font-serif text-3xl md:text-4xl tracking-tight mb-2">
          Five per row.
        </h2>
        <p className="text-[13px] text-charcoal/70 mb-6 max-w-2xl leading-relaxed">
          At <span className="font-mono">1240px</span> container width, five square cards fit at <span className="font-mono">~228px</span> each with 20px gaps.
          Text scales down proportionally so nothing feels crammed. This is what shoppers will see on a normal desktop.
        </p>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-5 mb-20">
          {SAMPLE_PRODUCTS.map((product, idx) => (
            <PeptideCard
              key={`compact-${product.sku}`}
              product={product}
              reveal={compactIdx === idx}
              interactive
              compact
              theme="select"
              onEnter={() => setCompactIdx(idx)}
              onLeave={() => setCompactIdx((p) => (p === idx ? null : p))}
            />
          ))}
        </div>

        {/* ---- SECTION 4: Supply palette test ---- */}
        <p className="overline text-oxblood mb-3">Section 4 · Supply palette · new warm image</p>
        <h2 className="font-serif text-3xl md:text-4xl tracking-tight mb-2">
          Supply — new image.
        </h2>
        <p className="text-[13px] text-charcoal/70 mb-6 max-w-2xl leading-relaxed">
          Your new oxblood/rose/pearl/champagne render layered with oxblood text and gold accents.
          No tint needed — the image is already warm and matches Supply's palette natively.
        </p>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-5 mb-20">
          {SAMPLE_PRODUCTS.map((product, idx) => (
            <PeptideCard
              key={`supply-${product.sku}`}
              product={product}
              reveal={supplyIdx === idx}
              interactive
              compact
              theme="supply"
              onEnter={() => setSupplyIdx(idx)}
              onLeave={() => setSupplyIdx((p) => (p === idx ? null : p))}
            />
          ))}
        </div>

        {/* ---- Notes ---- */}
        <div className="mt-4 border-t border-borderSubtle pt-8 max-w-2xl leading-relaxed grid gap-6">
          <div>
            <p className="font-mono uppercase tracking-[0.22em] text-oxblood mb-2 text-[12px]">
              Sizing notes
            </p>
            <ul className="list-disc pl-6 mt-1 text-[13px] text-charcoal/80 space-y-1">
              <li><b>Full size</b> ~380px — shown in Sections 1 &amp; 2. Great for the featured hero slot.</li>
              <li><b>Compact</b> ~228px — Sections 3 &amp; 4. Production default for the catalog grid.</li>
              <li>On tablet the grid collapses to 3-per-row, on mobile to 2-per-row.</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ---------- The card ---------- */

function PeptideCard({ product, reveal, interactive = false, compact = false, theme = "select", onEnter, onLeave }) {
  const { sku, name, subtitle, price, vialMg } = product;
  const t = THEMES[theme];

  // Sizing tokens — compact vs full-size (full-disclosure hierarchy)
  const s = compact
    ? {
        padTop: "top-3", padLeft: "left-3", padRight: "right-3", padBottom: "bottom-3",
        wordmark: "text-[10.5px]", bar: "h-[1px] w-6",
        ruoText: "text-[7.5px] px-1.5 py-[3px]",
        pill: "text-[7.5px]",
        productName: "text-[22px]",
        subtitleText: "text-[8.5px]",
        priceText: "text-[34px]",
        kitFormat: "text-[8.5px]",
        specChip: "text-[8px]",
        ctaText:  "text-[8.5px] px-2 py-1.5 gap-1",
        ctaIcon:  10,
        actionGap: "gap-1.5", actionInset: "left-3 right-3 bottom-3",
        contentPad: "pl-3 pr-3",
      }
    : {
        padTop: "top-5", padLeft: "left-5", padRight: "right-5", padBottom: "bottom-5",
        wordmark: "text-[15px]", bar: "h-[1px] w-10",
        ruoText: "text-[9px] px-2.5 py-1",
        pill: "text-[9px]",
        productName: "text-[36px]",
        subtitleText: "text-[11px]",
        priceText: "text-[56px]",
        kitFormat: "text-[11px]",
        specChip: "text-[10px]",
        ctaText:  "text-[10.5px] px-3 py-2.5 gap-1.5",
        ctaIcon:  12,
        actionGap: "gap-2", actionInset: "left-5 right-5 bottom-5",
        contentPad: "pl-5 pr-4",
      };

  return (
    <div
      className="relative aspect-square rounded-lg overflow-hidden shadow-[0_20px_60px_-20px_rgba(20,30,40,0.28)] cursor-pointer group transition-[border-color,box-shadow] duration-300"
      onMouseEnter={onEnter}
      onMouseLeave={onLeave}
      data-testid={`cloaked-card-${compact ? (theme === "supply" ? "supply-" : "compact-") : ""}${sku}`}
      style={{
        borderWidth: 1,
        borderStyle: "solid",
        borderColor: reveal ? t.text : `${t.text}33`,
      }}
    >
      {/* Background — theme-driven */}
      {t.bg ? (
        <img src={t.bg} alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover select-none pointer-events-none" draggable={false} />
      ) : (
        <div className="absolute inset-0 bg-white pointer-events-none" aria-hidden="true" />
      )}

      {/* Legibility scrim — softens the molecule behind the text */}
      {t.bg && (
        <div
          className="absolute inset-0 pointer-events-none"
          style={{ background: "linear-gradient(135deg, rgba(255,255,255,0.72) 0%, rgba(255,255,255,0.35) 55%, rgba(255,255,255,0.15) 100%)" }}
        />
      )}

      {/* Corner crosshairs */}
      <div className="absolute inset-0 pointer-events-none">
        <span className="absolute top-2 left-2 opacity-25" style={{ color: t.text }}>
          <svg width="10" height="10" viewBox="0 0 10 10" fill="none"><path d="M0 5h10M5 0v10" stroke="currentColor" strokeWidth="0.75"/></svg>
        </span>
        <span className="absolute bottom-2 right-2 opacity-25" style={{ color: t.text }}>
          <svg width="10" height="10" viewBox="0 0 10 10" fill="none"><path d="M0 5h10M5 0v10" stroke="currentColor" strokeWidth="0.75"/></svg>
        </span>
      </div>

      {/* Wordmark top-left */}
      <div className={`absolute ${s.padTop} ${s.padLeft} z-10`}>
        <p className={`font-mono ${s.wordmark} font-semibold leading-none tracking-[0.02em]`} style={{ color: t.text }}>
          BulkBuyKits<span className="font-normal opacity-70">.com</span>
        </p>
        <div className={`mt-1.5 ${s.bar}`} style={{ background: t.accent }} />
      </div>

      {/* RUO chip top-right */}
      <div className={`absolute ${s.padTop} ${s.padRight} z-10`}>
        <span className={`inline-flex items-center rounded-sm bg-white/80 backdrop-blur-sm font-mono uppercase tracking-[0.28em] shadow-sm ${s.ruoText}`}
              style={{ color: t.text, borderColor: `${t.text}30`, borderWidth: 1 }}>
          RUO
        </span>
      </div>

      {/* FULL-DISCLOSURE content — everything visible at rest */}
      <div className={`absolute inset-0 flex flex-col justify-center ${s.contentPad} pointer-events-none`}
           style={{ paddingTop: compact ? 44 : 76, paddingBottom: compact ? 44 : 76, textShadow: "0 1px 2px rgba(255,255,255,0.7)" }}>
        <div style={{ color: t.text }}>
          <h3 className={`font-sans font-bold uppercase ${s.productName} leading-[0.95] tracking-[0.01em]`}
              style={{ color: t.text, fontFamily: "Manrope, ui-sans-serif, system-ui" }}>
            {name}
          </h3>
          <p className={`font-serif italic ${s.subtitleText} leading-tight mt-0.5`} style={{ opacity: 0.7 }}>
            {subtitle}
          </p>

          <div className={`font-mono ${s.kitFormat} uppercase tracking-[0.24em] mt-2`} style={{ opacity: 0.85 }}>
            <span className="tabular-nums">3 × {vialMg}mg</span>
            <span className="mx-1.5" style={{ opacity: 0.4 }}>·</span>
            <span>Kit</span>
          </div>

          <p className={`font-sans font-bold ${s.priceText} leading-none tracking-[-0.02em] mt-2`}
             style={{ color: t.text, fontFamily: "Manrope, ui-sans-serif, system-ui" }}>
            <span className="tabular-nums">${price}</span>
          </p>

          <div className={`flex items-center gap-2 mt-2`} style={{ color: t.text }}>
            <span className="inline-block rounded-full" style={{ background: t.text, width: compact ? 4 : 5, height: compact ? 4 : 5, opacity: 0.85 }} aria-hidden="true" />
            <span className={`font-mono uppercase tracking-[0.24em] ${s.specChip}`} style={{ opacity: 0.85 }}>
              98% Purity · Mass · Content
            </span>
          </div>
        </div>
      </div>

      {/* Action row — slides up on hover */}
      <div className={`absolute ${s.actionInset} z-10 flex items-center ${s.actionGap} transition-all duration-300`}
           style={{
             opacity: reveal ? 1 : 0,
             pointerEvents: reveal ? "auto" : "none",
             transform: reveal ? "translateY(0)" : "translateY(8px)",
           }}>
        <button className={`flex-1 inline-flex items-center justify-center rounded-sm text-white font-mono uppercase tracking-[0.24em] font-semibold transition-colors shadow-md ${s.ctaText}`}
                style={{ background: t.ctaBg }}
                onMouseEnter={(e) => { e.currentTarget.style.background = t.ctaHover; e.currentTarget.style.color = t.text; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = t.ctaBg; e.currentTarget.style.color = "#fff"; }}
                data-testid={`quick-add-default-${sku}`}>
          <ShoppingBag size={s.ctaIcon} strokeWidth={2.4} />
          Add to Cart
        </button>
        <button className={`inline-flex items-center justify-center rounded-sm bg-white/85 backdrop-blur-sm font-mono uppercase tracking-[0.24em] font-semibold shadow-sm transition-colors ${s.ctaText}`}
                style={{ color: t.text, borderColor: `${t.text}40`, borderWidth: 1 }}
                onMouseEnter={(e) => { e.currentTarget.style.background = t.text; e.currentTarget.style.color = "#fff"; e.currentTarget.style.borderColor = t.text; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = "rgba(255,255,255,0.85)"; e.currentTarget.style.color = t.text; e.currentTarget.style.borderColor = `${t.text}40`; }}
                onClick={(e) => e.stopPropagation()}
                data-testid={`view-coa-${sku}`}>
          <FileText size={s.ctaIcon} strokeWidth={2.4} />
          COA
        </button>
      </div>
    </div>
  );
}
